import { inject, Injectable, signal } from '@angular/core';
import { Preferences } from '@capacitor/preferences'
import { ApiHttpService } from './api.service'
import { MatrixService } from './matrix.service'

@Injectable({
  providedIn: 'root'
})
export class AuthService {
    apiService = inject(ApiHttpService);
    matrixService = inject(MatrixService);

    private readonly TOKEN_KEY = 'auth-token';
    userData = signal<any>(null);

    constructor() {
    }

    login(userData: any) {
        return this.apiService.post('auth/login', userData);
    }

    async setToken(token: string) {
        await Preferences.set({
            key: this.TOKEN_KEY, value: token,
        });
    }


    async getToken(): Promise<string | null> {
        const {value} = await Preferences.get({key: this.TOKEN_KEY});
        return value;
    }

    async setUserData(data: any) {
        const currentData = await this.getUserData() || {};
        const newData = typeof data === 'string' ? JSON.parse(data) : data;
        const mergedData = { ...currentData, ...newData };

        await Preferences.set({
            key: 'user', value: JSON.stringify(mergedData),
        });
        this.userData.set(mergedData);
        if (mergedData?.matrixHomeServer && mergedData?.matrixAccessToken
          && mergedData?.matrixUserId) {
            const matrixDataChanged = currentData?.matrixAccessToken !== mergedData?.matrixAccessToken
                || currentData?.matrixUserId !== mergedData?.matrixUserId;
            if (!this.matrixService.client || matrixDataChanged) {
                await this.matrixService.initClient(mergedData.matrixHomeServer, mergedData.matrixAccessToken,
                    mergedData.matrixUserId);
            }
        }
    }

    async getUserData(): Promise<any> {
        const {value} = await Preferences.get({key: 'user'});
        return value ? JSON.parse(value) : null;
    }

    async isAuthenticated(): Promise<boolean> {
        const token = await this.getToken();
        const userData = await this.getUserData();
        return !!token;
    }

    async logout() {
        await Preferences.clear();
        await this.matrixService.stopClient();
        this.userData.set(null);
    }

    logoutApi() {
        let matrixAccessToken = this.userData().matrixAccessToken || '';
        return this.apiService.post('auth/logout', {matrixAccessToken});
    }

    terminateSession(sessionId: number) {
        return this.apiService.delete(`auth/sessions/${sessionId}`);
    }

    getMe() {
        return this.apiService.get('auth/me');
    }

    checkRoomAccess(roomId: string) {
        return this.apiService.get(`auth/access/${roomId}`);
    }

    forgotPasswordRequest(email: string) {
        return this.apiService.post('auth/forgot-password', { email });
    }

    forgotPasswordVerifyCode(email: string, code: string) {
        return this.apiService.post('auth/forgot-password/verify', { email, code });
    }

    forgotPasswordReset(email: string, code: string, password: string) {
        return this.apiService.post('auth/forgot-password/reset', { email, code, password });
    }

    updatePushStatus (pushStatus: any) {
        let matrixAccessToken = this.userData().matrixAccessToken || '';
        return this.apiService.get('auth/pushStatus?pushStatus=' + pushStatus + '&matrixAccessToken=' + matrixAccessToken);
    }
}
